/**
 * sirus/PvpPanel — R232q21 Phase 4.
 *
 * Sirus 3.3.5a-style PvP overview window. Toggled via the `H` micro-button
 * (panel flag `pvp`). Three sections:
 *   – Очки чести (lifetime + current honor + arena points)
 *   – Арена 2v2/3v3/5v5 (rating + games played + win %)
 *   – Поля боя (BG-specific honor / win streaks)
 *
 * Plus a «История последних боёв» list with 10 mock entries (win/loss,
 * battleground/arena, honor gained, kills/deaths).
 *
 * Pure mock UI — no realmClient. Numbers will eventually come from a
 * `pvp-store` slice.
 */

import { useEffect } from 'react';
import type { CSSProperties } from 'react';

import { SirusFrame } from './SirusFrame.js';
import { useUiPanels } from './MicroMenu.js';

/* ─────────────────────────────────────────────────────────────────
 * Mock data
 * ──────────────────────────────────────────────────────────────── */

const HONOR = {
  lifetime: 84_320,
  current: 12_450,
  weeklyKills: 142,
  arenaPoints: 1_870,
};

interface Bracket {
  size: '2v2' | '3v3' | '5v5';
  rating: number;
  played: number;
  won: number;
}

const ARENA: Bracket[] = [
  { size: '2v2', rating: 1842, played: 96, won: 58 },
  { size: '3v3', rating: 2024, played: 128, won: 81 },
  { size: '5v5', rating: 1605, played: 32, won: 14 },
];

interface BgStat {
  name: string;
  played: number;
  won: number;
  honor: number;
}

const BGS: BgStat[] = [
  { name: 'Ущелье Песни Войны',  played: 84,  won: 49, honor: 14_320 },
  { name: 'Низина Арати',        played: 112, won: 70, honor: 21_840 },
  { name: 'Альтеракская долина', played: 56,  won: 31, honor: 18_220 },
  { name: 'Око Бури',            played: 48,  won: 26, honor: 11_460 },
];

interface MatchEntry {
  id: string;
  kind: string;
  result: 'win' | 'loss';
  honor: number;
  k: number;
  d: number;
  ago: string;
}

const HISTORY: MatchEntry[] = [
  { id: 'm1',  kind: 'Низина Арати',        result: 'win',  honor: 320, k: 14, d: 2,  ago: '5 мин'  },
  { id: 'm2',  kind: 'Арена 3v3',           result: 'win',  honor: 0,   k: 4,  d: 0,  ago: '12 мин' },
  { id: 'm3',  kind: 'Арена 3v3',           result: 'loss', honor: 0,   k: 1,  d: 1,  ago: '18 мин' },
  { id: 'm4',  kind: 'Ущелье Песни Войны',  result: 'win',  honor: 280, k: 9,  d: 3,  ago: '34 мин' },
  { id: 'm5',  kind: 'Альтеракская долина', result: 'loss', honor: 175, k: 22, d: 8,  ago: '1 ч'    },
  { id: 'm6',  kind: 'Око Бури',            result: 'win',  honor: 305, k: 12, d: 5,  ago: '1 ч'    },
  { id: 'm7',  kind: 'Арена 2v2',           result: 'win',  honor: 0,   k: 2,  d: 0,  ago: '2 ч'    },
  { id: 'm8',  kind: 'Низина Арати',        result: 'win',  honor: 290, k: 11, d: 4,  ago: '3 ч'    },
  { id: 'm9',  kind: 'Ущелье Песни Войны',  result: 'loss', honor: 95,  k: 3,  d: 9,  ago: '4 ч'    },
  { id: 'm10', kind: 'Арена 5v5',           result: 'loss', honor: 0,   k: 1,  d: 1,  ago: 'вчера'  },
];

/* ─────────────────────────────────────────────────────────────────
 * Component
 * ──────────────────────────────────────────────────────────────── */

export function PvpPanel() {
  const close = () => useUiPanels.getState().set('pvp', false);

  /* ── Esc closes ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div role="dialog" aria-modal style={overlayStyle} onClick={close}>
      <div onClick={(e) => e.stopPropagation()}>
        <SirusFrame size="md" style={{ width: 600, height: 560, position: 'relative' }}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Битвы и подвиги</span>
            <button type="button" onClick={close} style={closeBtnStyle} aria-label="Закрыть">×</button>
          </div>

          <div style={bodyStyle}>
            {/* Honor section */}
            <Section title="Очки чести">
              <div style={honorGridStyle}>
                <Stat label="Всего убийств" value={HONOR.lifetime.toLocaleString('ru-RU')} accent />
                <Stat label="Чести (текущая неделя)" value={HONOR.current.toLocaleString('ru-RU')} />
                <Stat label="Убийств на этой неделе" value={String(HONOR.weeklyKills)} />
                <Stat label="Очков арены" value={HONOR.arenaPoints.toLocaleString('ru-RU')} accent />
              </div>
            </Section>

            {/* Arena section */}
            <Section title="Арена 2v2 / 3v3 / 5v5">
              <div style={{ display: 'flex', gap: 8 }}>
                {ARENA.map((b) => {
                  const pct = b.played === 0 ? 0 : Math.round((b.won / b.played) * 100);
                  return (
                    <div key={b.size} style={arenaCardStyle}>
                      <div style={arenaSizeStyle}>{b.size}</div>
                      <div style={arenaRatingStyle}>{b.rating}</div>
                      <div style={subStyle}>{b.played} боёв · {pct}% побед</div>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* BG section */}
            <Section title="Поля боя">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {BGS.map((b) => {
                  const pct = b.played === 0 ? 0 : Math.round((b.won / b.played) * 100);
                  return (
                    <div key={b.name} style={bgRowStyle}>
                      <span style={{ flex: 1, ...nameStyle }}>{b.name}</span>
                      <span style={subStyle}>{b.played} боёв</span>
                      <span style={subStyle}>{pct}% побед</span>
                      <span style={{ ...subStyle, color: '#FFD479', fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)' }}>
                        {b.honor.toLocaleString('ru-RU')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* History */}
            <Section title="История последних боёв">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {HISTORY.map((m) => (
                  <div key={m.id} style={historyRowStyle}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: m.result === 'win' ? '#7BC97B' : '#C45656',
                    }} />
                    <span style={{ flex: 1, ...nameStyle, fontSize: 12 }}>{m.kind}</span>
                    <span style={subStyle}>{m.k}/{m.d}</span>
                    {m.honor > 0 && (
                      <span style={{ ...subStyle, color: '#FFD479', fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)' }}>
                        +{m.honor}
                      </span>
                    )}
                    <span style={{ ...subStyle, minWidth: 50, textAlign: 'right' }}>{m.ago}</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </SirusFrame>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Building blocks
 * ──────────────────────────────────────────────────────────────── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={sectionTitleStyle}>{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={statCardStyle}>
      <div style={subStyle}>{label}</div>
      <div style={{
        fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
        fontSize: 18,
        color: accent ? '#FFD479' : 'var(--aiwari-fg, #E8E1DA)',
      }}>
        {value}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Styles
 * ──────────────────────────────────────────────────────────────── */

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 40,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: 8,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const titleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 18,
  letterSpacing: 0.8,
  color: 'var(--aiwari-gold-soft, #FFD479)',
};

const closeBtnStyle: CSSProperties = {
  width: 28, height: 28,
  background: 'transparent',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontSize: 18, lineHeight: 1, cursor: 'pointer',
  fontFamily: 'inherit',
};

const bodyStyle: CSSProperties = {
  height: 480,
  marginTop: 10,
  overflowY: 'auto',
  fontFamily: 'var(--aiwari-font-body, "EB Garamond", Georgia, serif)',
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const sectionTitleStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  letterSpacing: 0.5,
  color: 'var(--aiwari-gold-soft, #FFD479)',
  marginBottom: 6,
  paddingBottom: 4,
  borderBottom: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const honorGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 8,
};

const statCardStyle: CSSProperties = {
  padding: '6px 10px',
  background: 'rgba(11,9,7,0.4)',
  border: '1px solid #2A2014',
};

const arenaCardStyle: CSSProperties = {
  flex: 1,
  textAlign: 'center',
  padding: '8px 6px',
  background: 'linear-gradient(180deg, #1F1813, #0B0907)',
  border: '1px solid var(--aiwari-frame-edge, #5C4520)',
};

const arenaSizeStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 14,
  letterSpacing: 0.5,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
};

const arenaRatingStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
  fontSize: 22,
  color: '#FFD479',
  margin: '4px 0',
};

const bgRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '5px 8px',
  background: 'rgba(11,9,7,0.4)',
  border: '1px solid #2A2014',
};

const historyRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '4px 8px',
  background: 'rgba(11,9,7,0.4)',
  border: '1px solid #2A2014',
};

const nameStyle: CSSProperties = {
  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
  fontSize: 13,
  color: 'var(--aiwari-fg, #E8E1DA)',
};

const subStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--aiwari-fg-muted, #B19B7E)',
  fontStyle: 'italic',
};

export default PvpPanel;
